import { useEffect, useMemo, useState } from "react";
import {
  Button,
  Card,
  Col,
  DatePicker,
  Form,
  Input,
  List,
  Modal,
  Row,
  Select,
  Space,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import {
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import {
  collection,
  onSnapshot,
  orderBy,
  query,
  where,
} from "firebase/firestore";
import { db } from "../../lib/firebase";
import { useAuth } from "../../lib/auth";
import { EmptyState } from "../EmptyState";
import SubtaskFormModal from "./SubtaskFormModal";
import type {
  Stage,
  SubtaskItem,
  TaskItem,
} from "../../lib/types";
import type { UserOption } from "./types";

interface TaskFormState {
  title: string;
  description: string;
  dependencies: string;
  stageId: string;
  startDate: string;
  endDate: string;
}

interface PlanTabProps {
  projectId: string;
  tasks: TaskItem[];
  subtasks: SubtaskItem[];
  users: UserOption[];
  taskForm: TaskFormState;
  setTaskForm: React.Dispatch<React.SetStateAction<TaskFormState>>;
  editingTask: TaskItem | null;
  taskModalOpen: boolean;
  onOpenNewTask: () => void;
  onOpenEditTask: (task: TaskItem) => void;
  onSaveTask: () => Promise<void> | void;
  onDeleteTask: (task: TaskItem) => void | Promise<void>;
  onSelectTask: (task: TaskItem) => void;
  onCloseTaskModal: () => void;
}

const UNSTAGED_KEY = "__unstaged__";

export default function PlanTab({
  projectId,
  tasks,
  subtasks,
  users,
  taskForm,
  setTaskForm,
  editingTask,
  taskModalOpen,
  onOpenNewTask,
  onOpenEditTask,
  onSaveTask,
  onDeleteTask,
  onSelectTask,
  onCloseTaskModal,
}: PlanTabProps) {
  const { isAdmin, canWrite } = useAuth();
  const [stages, setStages] = useState<Stage[]>([]);
  const [subtaskParentTask, setSubtaskParentTask] = useState<TaskItem | null>(
    null,
  );

  // Subscribe to this project's stages so we can render group headers and
  // populate the Stage select in the New/Edit Task form.
  useEffect(() => {
    const stagesQuery = query(
      collection(db, "projectStages"),
      where("projectId", "==", projectId),
      orderBy("order", "asc"),
    );
    const unsubscribe = onSnapshot(stagesQuery, (snapshot) => {
      const data: Stage[] = [];
      snapshot.forEach((stageDoc) => {
        const raw = stageDoc.data();
        data.push({
          id: stageDoc.id,
          projectId: raw.projectId,
          name: raw.name ?? "",
          order: typeof raw.order === "number" ? raw.order : 0,
          createdAt: raw.createdAt ?? "",
        });
      });
      setStages(data);
    });
    return () => unsubscribe();
  }, [projectId]);

  const hasStages = stages.length > 0;

  // Group tasks by stageId in stage.order order, with an "Unstaged" bucket
  // at the end for tasks whose stageId is empty or no longer matches a stage.
  const grouped = useMemo(() => {
    const stageIds = new Set(stages.map((s) => s.id));
    const groups: Array<{
      key: string;
      title: string;
      tasks: TaskItem[];
    }> = stages.map((stage) => ({
      key: stage.id,
      title: stage.name,
      tasks: [],
    }));
    const unstaged: TaskItem[] = [];

    tasks.forEach((task) => {
      if (task.stageId && stageIds.has(task.stageId)) {
        const bucket = groups.find((g) => g.key === task.stageId);
        if (bucket) bucket.tasks.push(task);
      } else {
        unstaged.push(task);
      }
    });

    if (unstaged.length > 0) {
      groups.push({ key: UNSTAGED_KEY, title: "Unstaged", tasks: unstaged });
    }

    return groups;
  }, [stages, tasks]);

  const stageOptions = useMemo(
    () => stages.map((stage) => ({ value: stage.id, label: stage.name })),
    [stages],
  );

  // OAIP-44: resolve structured `dependsOnTaskIds` to titles for read-only
  // display (applied-template tasks carry these). The editable picker + the
  // free-text migration land in OAIP-119.
  const taskTitleById = useMemo(() => {
    const map = new Map<string, string>();
    tasks.forEach((t) => map.set(t.id, t.title));
    return map;
  }, [tasks]);

  const renderTaskItem = (task: TaskItem) => {
    const taskSubtasks = subtasks.filter((s) => s.taskId === task.id);
    const completedSubtaskCount = taskSubtasks.filter(
      (s) => s.status === "done",
    ).length;
    const dependsOnTitles = (task.dependsOnTaskIds || [])
      .map((id) => taskTitleById.get(id))
      .filter((t): t is string => !!t);
    return (
      <List.Item
        key={task.id}
        style={{ cursor: "pointer" }}
        onClick={() => onSelectTask(task)}
        actions={[
          ...(canWrite
            ? [
                <Tooltip key="add-subtask" title="Add subtask">
                  <Button
                    type="text"
                    icon={<PlusOutlined />}
                    aria-label={`Add new subtask to task ${task.title}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      setSubtaskParentTask(task);
                    }}
                  >
                    New Subtask
                  </Button>
                </Tooltip>,
                <Tooltip key="edit" title="Edit task">
                  <Button
                    type="text"
                    icon={<EditOutlined />}
                    aria-label={`Edit task ${task.title}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      onOpenEditTask(task);
                    }}
                  />
                </Tooltip>,
              ]
            : []),
          ...(isAdmin
            ? [
                <Tooltip key="delete" title="Delete task">
                  <Button
                    type="text"
                    danger
                    icon={<DeleteOutlined />}
                    aria-label={`Delete task ${task.title}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      onDeleteTask(task);
                    }}
                  />
                </Tooltip>,
              ]
            : []),
        ]}
      >
        <List.Item.Meta
          title={
            <Space>
              <Typography.Text strong>{task.title}</Typography.Text>
              {task.completedAt && <Tag color="success">Completed</Tag>}
            </Space>
          }
          description={
            <Space direction="vertical" size={0}>
              <Typography.Text type="secondary" style={{ fontSize: "12px" }}>
                {task.startDate ? `Start ${task.startDate}` : "No start date"}{" "}
                ·{" "}
                {task.endDate ? `End ${task.endDate}` : "No end date"}
              </Typography.Text>
              <Typography.Text type="secondary" style={{ fontSize: "12px" }}>
                {completedSubtaskCount}/{taskSubtasks.length} subtasks
                completed · {(task.comments || []).length} comments
              </Typography.Text>
              {task.description && (
                <Typography.Text
                  type="secondary"
                  ellipsis
                  style={{ fontSize: "12px", maxWidth: "600px" }}
                >
                  {task.description}
                </Typography.Text>
              )}
              {dependsOnTitles.length > 0 && (
                <Typography.Text type="secondary" style={{ fontSize: "12px" }}>
                  Depends on: {dependsOnTitles.join(", ")}
                </Typography.Text>
              )}
            </Space>
          }
        />
      </List.Item>
    );
  };

  return (
    <Space direction="vertical" size="large" style={{ width: "100%" }}>
      <Card
        title="Project Plan"
        extra={
          canWrite ? (
            <Space>
              {!hasStages && (
                <Typography.Text type="secondary" style={{ fontSize: "12px" }}>
                  Add at least one stage in Project Details first.
                </Typography.Text>
              )}
              <Tooltip
                title={
                  hasStages
                    ? ""
                    : "Add at least one stage in Project Details first."
                }
              >
                <Button
                  type="primary"
                  onClick={onOpenNewTask}
                  disabled={!hasStages}
                  aria-label="Create new task"
                >
                  New Task
                </Button>
              </Tooltip>
            </Space>
          ) : null
        }
      >
        {tasks.length === 0 ? (
          <EmptyState
            description={
              hasStages
                ? canWrite
                  ? "No tasks yet. Click 'New Task' to create one."
                  : "No tasks yet."
                : "No tasks yet. Add at least one stage in Project Details first."
            }
            action={
              hasStages && canWrite
                ? { label: "New Task", onClick: onOpenNewTask }
                : undefined
            }
          />
        ) : (
          <Space direction="vertical" size="large" style={{ width: "100%" }}>
            {grouped.map((group) =>
              group.tasks.length === 0 ? null : (
                <div key={group.key}>
                  <Typography.Title
                    level={5}
                    style={{ marginTop: 0, marginBottom: 8 }}
                  >
                    {group.title}
                  </Typography.Title>
                  <List
                    aria-label={`Tasks in stage ${group.title}`}
                    dataSource={group.tasks}
                    renderItem={renderTaskItem}
                  />
                </div>
              ),
            )}
          </Space>
        )}
      </Card>

      <Modal
        title={editingTask ? "Edit Task" : "New Task"}
        open={taskModalOpen}
        onCancel={onCloseTaskModal}
        onOk={onSaveTask}
        okText={editingTask ? "Save" : "Create"}
        okButtonProps={{
          disabled: !taskForm.title.trim() || !taskForm.stageId,
        }}
        destroyOnClose
      >
        <Form layout="vertical">
          <Form.Item label="Task title" required>
            <Input
              value={taskForm.title}
              onChange={(event) =>
                setTaskForm((prev) => ({ ...prev, title: event.target.value }))
              }
              placeholder="Task title"
            />
          </Form.Item>
          <Form.Item label="Stage" required>
            <Select
              value={taskForm.stageId || undefined}
              onChange={(value) =>
                setTaskForm((prev) => ({ ...prev, stageId: value || "" }))
              }
              options={stageOptions}
              placeholder={
                hasStages
                  ? "Pick a stage"
                  : "No stages yet — add one in Project Details"
              }
              disabled={!hasStages}
              aria-label="Task stage"
            />
          </Form.Item>
          <Form.Item label="Task description">
            <Input.TextArea
              value={taskForm.description}
              onChange={(event) =>
                setTaskForm((prev) => ({
                  ...prev,
                  description: event.target.value,
                }))
              }
              autoSize={{ minRows: 2 }}
            />
          </Form.Item>
          <Form.Item label="Dependencies">
            <Input
              value={taskForm.dependencies}
              onChange={(event) =>
                setTaskForm((prev) => ({
                  ...prev,
                  dependencies: event.target.value,
                }))
              }
            />
          </Form.Item>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label="Start date">
                <DatePicker
                  value={taskForm.startDate ? dayjs(taskForm.startDate) : null}
                  onChange={(date) =>
                    setTaskForm((prev) => ({
                      ...prev,
                      startDate: date ? date.format("YYYY-MM-DD") : "",
                    }))
                  }
                  style={{ width: "100%" }}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="End date">
                <DatePicker
                  value={taskForm.endDate ? dayjs(taskForm.endDate) : null}
                  onChange={(date) =>
                    setTaskForm((prev) => ({
                      ...prev,
                      endDate: date ? date.format("YYYY-MM-DD") : "",
                    }))
                  }
                  style={{ width: "100%" }}
                />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>

      <SubtaskFormModal
        open={!!subtaskParentTask}
        parentTask={subtaskParentTask}
        users={users}
        onCancel={() => setSubtaskParentTask(null)}
      />
    </Space>
  );
}
